import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMessage } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Loader } from "../../components/Blocks";

const LABELS = {
  applications: "Candidatures",
  applications_new: "Candidatures nouvelles",
  profiles: "Profils",
  partners: "Partenaires",
  events: "Événements",
  challenges: "Challenges",
  challenge_entries: "Entrées challenge",
  media_assets: "Médias",
  media_published: "Médias publiés",
  media_consents: "Consentements",
  consents_revoked: "Consentements révoqués",
  users: "Utilisateurs",
  audit_logs: "Entrées d'audit",
  collaborators: "Collaborateurs",
  demo_records: "Enregistrements DÉMO",
};

export default function AdminDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [demo, setDemo] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [purging, setPurging] = useState(false);

  const load = async () => {
    const [stats, demoData] = await Promise.all([api.get("/admin/stats"), api.get("/admin/demo-data")]);
    setData(stats.data);
    setDemo(demoData.data);
  };

  useEffect(() => {
    load().catch((e) => toast.error(errMessage(e)));
  }, []);

  const purge = async () => {
    setPurging(true);
    try {
      const { data: res } = await api.delete("/admin/demo-data");
      toast.success(`${res.total} enregistrement(s) de démonstration supprimé(s)`);
      setConfirming(false);
      await load();
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setPurging(false);
    }
  };

  if (!data || !demo) return <Loader />;

  return (
    <div data-testid="admin-dashboard">
      <h1 className="vb-display text-3xl lg:text-4xl font-bold">Dashboard</h1>
      <p className="mt-3 text-sm text-neutral-500">
        Stockage média : <span className="text-white">{data.media_storage.provider}</span> · production{" "}
        {data.media_storage.production_ready ? "prête" : "non configurée (démo)"}
      </p>

      <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {Object.entries(data.counts).map(([k, v]) => (
          <div key={k} className="vb-card p-5" data-testid={`admin-stat-${k}`}>
            <div className="vb-label">{LABELS[k] || k}</div>
            <div className="vb-display text-3xl mt-3">{v}</div>
          </div>
        ))}
      </div>

      {/* Demo data governance */}
      <div className="mt-14 vb-card p-6 lg:p-8" data-testid="admin-demo-panel">
        <div className="vb-label">Gouvernance des données de démonstration</div>
        <h2 className="vb-display text-2xl mt-3">Nettoyage avant production</h2>
        <p className="mt-3 text-sm text-neutral-400 max-w-3xl">
          Tous les enregistrements marqués <span className="text-white">is_demo</span> peuvent être
          retirés en une seule opération. Les comptes utilisateurs, les journaux d'audit et le contenu
          réel ne sont jamais touchés. Action irréversible, journalisée dans l'audit.
        </p>

        <div className="mt-6 flex flex-wrap gap-3" data-testid="admin-demo-counts">
          {Object.entries(demo.counts).map(([k, v]) => (
            <span
              key={k}
              className={`text-xs px-3 py-1.5 border ${v > 0 ? "border-[#FF2E63]/40 text-[#FF2E63]" : "border-white/10 text-neutral-600"}`}
              data-testid={`admin-demo-count-${k}`}
            >
              {LABELS[k] || k} : {v}
            </span>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-4">
          <div className="text-sm text-neutral-400">
            Total démo : <span className="text-white" data-testid="admin-demo-total">{demo.total}</span>
          </div>
          {user?.security_role === "super_admin" ? (
            confirming ? (
              <div className="flex flex-wrap gap-3">
                <button onClick={purge} disabled={purging} className="vb-btn vb-btn-primary !py-2 disabled:opacity-40" data-testid="admin-demo-purge-confirm">
                  {purging ? "Suppression…" : `Confirmer la suppression (${demo.total})`}
                </button>
                <button onClick={() => setConfirming(false)} className="vb-btn vb-btn-ghost !py-2" data-testid="admin-demo-purge-cancel">
                  Annuler
                </button>
              </div>
            ) : (
              <button
                onClick={() => setConfirming(true)}
                disabled={demo.total === 0}
                className="vb-btn vb-btn-ghost !py-2 disabled:opacity-40"
                data-testid="admin-demo-purge"
              >
                Supprimer tout le contenu de démonstration
              </button>
            )
          ) : (
            <span className="text-xs text-neutral-600">Action réservée au super_admin.</span>
          )}
        </div>
      </div>
    </div>
  );
}
